import { useCallback, useEffect, useRef, useState } from 'react';
import { usePermission } from './use-permission';

export interface UseGatedFetchOptions<T> {
  module: string;
  resource: string;
  fetch: () => Promise<T[]>;
  enabled?: boolean;
  /**
   * Re-run the fetch whenever any of these values change — use for fetches
   * keyed to a route param (e.g. `[employeeId]`). The hook always invokes the
   * latest `fetch` reference, so the array only needs the values that should
   * trigger a reload, not the `fetch` closure itself.
   */
  deps?: unknown[];
}

export interface UseGatedFetchResult<T> {
  canRead: boolean;
  data: T[];
  loading: boolean;
  /** Re-run the fetch on demand (e.g. after a create/upload/delete mutation). */
  refetch: () => Promise<void>;
}

/**
 * Permission-gated fetcher. Only invokes `fetch` when the caller holds
 * `<module>:<resource>:read`. When permission is absent, returns empty data
 * without making any request — the consuming view should render a read-only
 * label, an empty state, or hide the section, rather than letting the user act
 * on data they can't see.
 *
 * The hook captures the latest `fetch` reference via a ref so callers can pass
 * an inline arrow (e.g. `() => getLeaveRequests({ employeeId: id })`) without
 * causing a re-fetch on every render. Pass `deps` to refetch when a keyed value
 * (such as a route param) changes, and call `refetch()` to reload after a
 * mutation.
 */
export function useGatedFetch<T>({
  module,
  resource,
  fetch,
  enabled = true,
  deps = [],
}: UseGatedFetchOptions<T>): UseGatedFetchResult<T> {
  const canRead = usePermission(module, resource, 'read');
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;

  // Guards a manual refetch from clobbering state after the component unmounts
  // or after a newer load/refetch has superseded it.
  const runIdRef = useRef(0);

  const refetch = useCallback(async () => {
    if (!enabled || !canRead) {
      setData([]);
      setLoading(false);
      return;
    }
    const runId = ++runIdRef.current;
    setLoading(true);
    try {
      const result = await fetchRef.current();
      if (runId === runIdRef.current) setData(result);
    } catch {
      // Swallow — the consuming view falls through to the same empty-state
      // rendering path it uses when the caller lacks read permission.
      if (runId === runIdRef.current) setData([]);
    } finally {
      if (runId === runIdRef.current) setLoading(false);
    }
  }, [canRead, enabled]);

  useEffect(() => {
    if (!enabled || !canRead) {
      setData([]);
      setLoading(false);
      return;
    }
    const runId = ++runIdRef.current;
    setLoading(true);
    fetchRef
      .current()
      .then((result) => {
        if (runId === runIdRef.current) setData(result);
      })
      .catch(() => {
        if (runId === runIdRef.current) setData([]);
      })
      .finally(() => {
        if (runId === runIdRef.current) setLoading(false);
      });
    return () => {
      // Invalidate this run so a late resolution can't overwrite newer state.
      runIdRef.current++;
    };
    // `deps` is caller-supplied (keyed values like a route param); the latest
    // `fetch` is always read via fetchRef, so it intentionally isn't a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead, enabled, ...deps]);

  return { canRead, data, loading, refetch };
}
